package no.vareflow.web

import no.vareflow.app.domain.*
import kotlin.random.Random

// Mer → Utseende, Utskriftsliste, Stikkprøvekontroll and Datakvalitet: the same tools as in the
// phone's Mer / Admin screens (ThemePickerDialog, PrintableCategoryDialog, SpotCheckDialog,
// "Datakvalitet" in AdminScreen).

// ---------------------------------------------------------------- Utseende (fargetema)

/** Same themes as the phone (VareflowTheme): key, label, description, nav, page, primary. */
class WebTheme(
    val key: String,
    private val labelNb: String, private val labelEn: String,
    private val descriptionNb: String, private val descriptionEn: String,
    val nav: String, val page: String, val primary: String
) {
    val label: String get() = t(labelNb, labelEn)
    val description: String get() = t(descriptionNb, descriptionEn)
}

val WEB_THEMES = listOf(
    WebTheme("WARM_LIGHT", "Varm lys", "Warm light", "Kremfarget topp/bunn med varm oransje innhold", "Cream top/bottom with warm orange content", "#FFF9F3", "#FFF8F1", "#FF7A00"),
    WebTheme("APRICOT", "Aprikos", "Apricot", "Myk aprikos topp/bunn med varm oransje innhold", "Soft apricot top/bottom with warm orange content", "#FCEAD7", "#F6A43A", "#C96A04"),
    WebTheme("DARK", "Mørk", "Dark", "Mørk grafitt topp/bunn med varm oransje innhold", "Dark graphite top/bottom with warm orange content", "#2B2A28", "#F59A23", "#FF9B22"),
    WebTheme("GLOD", "Glød", "Glow", "Myke fargeflater i blågrått, fersken og oransje under matte glassbokser (standard)", "Soft areas of blue-grey, peach and orange under frosted glass boxes (default)", "#7C8C9C", "#F09A62", "#2F6E9E"),
    WebTheme("SKUMRING", "Skumring", "Dusk", "Solnedgang som glir over i plomme, matte glassbokser og stålblå knapper", "Sunset fading into plum, frosted glass boxes and steel-blue buttons", "#6F7F90", "#D9767A", "#2F6E9E"),
    WebTheme("LAVENDEL", "Lavendel", "Lavender", "Lys lavendel med gjennomsiktige fliser og tynne hvite runde knapper", "Light lavender with see-through tiles and thin white round buttons", "#8C9BF2", "#B4A5EF", "#FFFFFF")
)

/** Theme for users who have not chosen one, and for the login page. */
const val DEFAULT_THEME = "GLOD"

private fun themeKey(userId: String) = "vareflow.theme_$userId"

/** The user's theme in this browser (the phone keeps it per user too). */
fun loadTheme(userId: String): String =
    runCatching { window.localStorage.getItem(themeKey(userId)) as String? }.getOrNull()
        ?.takeIf { key -> WEB_THEMES.any { it.key == key } } ?: DEFAULT_THEME

fun applyTheme(userId: String?) {
    val key = if (userId == null) DEFAULT_THEME else loadTheme(userId)
    document.body.setAttribute("data-theme", key.lowercase())
    document.querySelector("meta[name=theme-color]")?.setAttribute("content", WEB_THEMES.first { it.key == key }.nav)
}

fun WebApp.showThemePicker(user: UserAccount) {
    openDialog(t("Fargetema", "Colour theme")) { close ->
        p(t("Temaet lagres på kontoen din og følger deg til andre enheter.", "The theme is saved on your account and follows you to other devices."), "muted small")
        val selected = loadTheme(user.id)
        div("picker-list") {
            WEB_THEMES.forEach { theme ->
                el("button", "picker-item" + if (theme.key == selected) " selected" else "") {
                    attr("type", "button")
                    div("theme-swatch") {
                        div("sw-bar").attr("style", "background:${theme.nav}")
                        div("sw-page").attr("style", "background:${theme.page}")
                        div("sw-bar").attr("style", "background:${theme.nav}")
                    }
                    div("grow") {
                        div("strong", theme.label)
                        div("muted small", theme.description)
                    }
                    span("theme-check", if (theme.key == selected) "✓" else "").attr("style", "color:${theme.primary}")
                    onClick {
                        runCatching { window.localStorage.setItem(themeKey(user.id), theme.key) }
                        toast(t("Tema endret til ${theme.label}.", "Theme changed to ${theme.label}."))
                        requestRender()
                    }
                }
            }
        }
        dialogActions { button(t("Ferdig", "Done"), "btn primary") { close() } }
    }
}

// ---------------------------------------------------------------- Utskriftsliste / tellingsark

fun WebApp.showPrintDialog() {
    val categories = core.orderedCategories()
    val state = object { var category = categories.firstOrNull().orEmpty(); var paper = "A4"; var scale = 100 }
    openDialog(t("Utskriftsliste / tellingsark", "Print list / count sheet")) { close ->
        val products = core.orderedProducts(state.category).filter { it.active }
        div("strong small", t("Hovedvare", "Main category"))
        div("chip-row wrap") {
            categories.forEach { c -> chip(c, c == state.category) { state.category = c; requestRender() } }
        }
        div("row") {
            span("strong small", t("Papir", "Paper"))
            listOf("A4", "Letter").forEach { size -> chip(size, state.paper == size) { state.paper = size; requestRender() } }
        }
        div("row") {
            span("strong small", t("Skala", "Scale"))
            div("chip-row wrap") {
                listOf(80, 90, 100, 110, 120).forEach { s -> chip("$s%", state.scale == s) { state.scale = s; requestRender() } }
            }
        }
        p(t("${products.size} produkter · ${state.paper} · ${state.scale}% · VF-kode gjør gjenkjenning sikrere ved innskanning",
            "${products.size} products · ${state.paper} · ${state.scale}% · the VF code makes scanning more reliable"), "muted tiny")
        div("print-preview") {
            el("table", "lines") {
                el("thead") { el("tr") { el("th", "", t("Varenr.", "Item no.")); el("th", "", t("Produkt", "Product")); el("th", "", t("Enhet", "Unit")); el("th", "num", t("Antall", "Quantity")) } }
                el("tbody") {
                    groupByUndergruppe(products).forEach { (undergruppe, group) ->
                    if (undergruppe.isNotEmpty()) el("tr", "subgroup-row") { el("td", "", undergruppe).attr("colspan", "4") }
                    group.forEach { product ->
                        el("tr") {
                            el("td", "muted", product.varenummer.ifBlank { "–" })
                            el("td") {
                                div("", product.name)
                                div("muted tiny", vfCode(product.id))
                            }
                            el("td", "muted", product.unit)
                            el("td", "num") { div("print-box") }
                        }
                    }
                    }
                }
            }
        }
        dialogActions {
            button(t("Lukk", "Close"), "btn text") { close() }
            button(t("Skriv ut", "Print"), "btn primary", enabled = products.isNotEmpty()) { printSheet(state.category, products, state.paper, state.scale) }
        }
    }
}

private fun vfCode(productId: String) = "VF" + productId.replace("-", "").take(8).uppercase()

private fun esc(text: String) = text.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;").replace("\"", "&quot;")

/** Opens the sheet in a new window and starts the browser's print dialog (same layout as the phone's PDF). */
private fun WebApp.printSheet(category: String, products: List<Product>, paper: String, scale: Int) {
    val win = window.open("", "_blank")
    if (win == null) {
        toast(t("Nettleseren blokkerte utskriftsvinduet. Tillat popup-vinduer for denne siden.", "The browser blocked the print window. Allow pop-ups for this page."))
        return
    }
    val size = if (paper == "Letter") "letter" else "A4"
    val rows = groupByUndergruppe(products).joinToString("") { (undergruppe, group) ->
        (if (undergruppe.isEmpty()) "" else "<tr class=\"ug\"><td colspan=\"4\">${esc(undergruppe)}</td></tr>") +
            group.joinToString("") { p ->
                "<tr><td>${esc(p.varenummer.ifBlank { "–" })}</td><td>${esc(p.name)}<div class=\"code\">${vfCode(p.id)}</div></td>" +
                    "<td>${esc(p.unit)}</td><td class=\"box\"><div></div></td></tr>"
            }
    }
    val html = """<!doctype html><html lang="nb"><head><meta charset="utf-8"><title>VareFlow · ${esc(category)}</title>
<style>
@page { size: $size; margin: 14mm 12mm; }
body { font: ${10.5 * scale / 100}pt/1.25 system-ui, "Segoe UI", Roboto, sans-serif; color: #111; margin: 0; }
h1 { font-size: ${19 * scale / 100}pt; margin: 0 0 2pt; }
.sub { font-size: 8pt; color: #555; margin-bottom: 10pt; }
table { width: 100%; border-collapse: collapse; }
th { text-align: left; font-size: 9pt; border-bottom: 1px solid #000; padding: 4pt 4pt; }
td { border-bottom: .6pt solid #999; padding: ${5 * scale / 100}pt 4pt; vertical-align: middle; }
thead { display: table-header-group; }
tr { page-break-inside: avoid; }
.code { font-size: 7pt; color: #555; }
td.box { width: 60pt; }
td.box div { border: .8pt solid #333; height: ${18 * scale / 100}pt; border-radius: 2pt; }
tr.ug td { font-weight: 700; font-size: ${11.5 * scale / 100}pt; border-bottom: 1px solid #000; padding-top: ${10 * scale / 100}pt; page-break-after: avoid; }
</style></head><body>
<h1>VareFlow · ${esc(category)}</h1>
<div class="sub">$paper · $scale% · Fyll inn antall i feltet</div>
<table><thead><tr><th>Varenr.</th><th>Produkt</th><th>Enhet</th><th>Antall</th></tr></thead><tbody>$rows</tbody></table>
<script>window.onload = function () { window.focus(); window.print(); };</script>
</body></html>"""
    win.document.open()
    win.document.write(html)
    win.document.close()
}

// ---------------------------------------------------------------- Stikkprøvekontroll

private class SpotCandidate(val product: Product, val departmentName: String?, val expected: Double, val sessionId: String?)

fun WebApp.showSpotCheck(user: UserAccount) {
    // A counted line in the open month; otherwise from the last closed month (as on the phone).
    val open = shownOpenSession()
    val current = open?.let { session ->
        val departments = core.departmentsForSession(session.id)
        core.products.flatMap { product ->
            departments.mapNotNull { d ->
                core.entry(session.id, product.id, d.id)?.quantity?.let { SpotCandidate(product, d.name, it, session.id) }
            }
        }
    }.orEmpty()
    val latest = core.sessions.firstOrNull()
    val fallback = latest?.lines.orEmpty().mapNotNull { line ->
        core.products.firstOrNull { it.id == line.productId }?.let { SpotCandidate(it, line.departmentName, line.quantity, latest?.id) }
    }
    val candidates = current.ifEmpty { fallback }
    val chosen = candidates.takeIf { it.isNotEmpty() }?.let { it[Random.nextInt(it.size)] }
    val state = object { var checked = ""; var result: Boolean? = null }
    openDialog(t("Valgfri stikkprøvekontroll", "Optional spot check")) { close ->
        if (chosen == null) {
            p(t("Ingen telte produkter å kontrollere ennå.", "No counted products to check yet."))
            dialogActions { button(t("Lukk", "Close"), "btn text") { close() } }
            return@openDialog
        }
        p(t("Tell produktet på nytt uten å se den registrerte verdien:", "Count the product again without seeing the recorded value:"))
        h2(chosen.product.name, "dialog-subtitle")
        p(t("Enhet", "Unit") + ": ${chosen.product.unit}" + (chosen.departmentName?.let { " · " + t("Avdeling", "Department") + ": $it" } ?: ""), "muted small")
        field(t("Kontrolltall", "Check quantity"), state.checked, "spot-qty") { raw ->
            state.checked = raw.filter { it.isDigit() || it == ',' || it == '.' }
        }.also { it.on("change") { requestRender() } }
        state.result?.let { matches ->
            div(if (matches) "ok-text strong" else "error-text",
                if (matches) t("Treff ✓ Registrert verdi: ${formatQuantity(chosen.expected)}", "Match ✓ Recorded value: ${formatQuantity(chosen.expected)}")
                else {
                    val checked = WebApp.parseQuantity(state.checked)?.let(::formatQuantity) ?: "?"
                    t("Avvik: registrert ${formatQuantity(chosen.expected)}, kontrollert $checked", "Difference: recorded ${formatQuantity(chosen.expected)}, checked $checked")
                })
        }
        p(t("Stikkprøven er frivillig og blokkerer ikke tellingen.", "The spot check is optional and does not block the count."), "muted tiny")
        dialogActions {
            button(t("Lukk", "Close"), "btn text") { close() }
            button(if (state.result == null) t("Kontroller", "Check") else t("Lagret", "Saved"), "btn primary", enabled = state.result == null) {
                val qty = WebApp.parseQuantity(state.checked)
                if (qty == null) { toast(t("Skriv inn kontrolltallet.", "Enter the check quantity.")); return@button }
                val matches = kotlin.math.abs(qty - chosen.expected) < 0.0001
                state.result = matches
                core.addSpotCheck(
                    SpotCheckRecord(
                        id = randomId(), sessionId = chosen.sessionId, productId = chosen.product.id, productName = chosen.product.name,
                        expectedQty = chosen.expected, checkedQty = qty, checkerUserId = user.id, checkerName = user.name,
                        createdAt = nowMillis(), matches = matches
                    )
                )
                requestRender()
            }
        }
    }
}

// ---------------------------------------------------------------- Datakvalitet

fun WebApp.canReviewQuality(user: UserAccount) =
    user.role == Role.ADMIN || AccessRules.hasPermission(user, Permission.PRODUCT_REVIEW_QUALITY)

fun WebApp.qualityProducts(): List<Product> =
    core.products.filter { it.importStatus.equals("Kontroller", ignoreCase = true) }
        .sortedWith { a, b -> if (a.category != b.category) compareNorwegian(a.category, b.category) else compareNorwegian(a.name, b.name) }

fun WebApp.renderQuality(root: El, user: UserAccount) {
    root.button(t("‹ Mer", "‹ More"), "btn text back") { moreView = "main"; requestRender() }
    val list = qualityProducts()
    root.pageHeader(t("Datakvalitet", "Data quality"), t("${list.size} produkter fra importen må kontrolleres", "${list.size} products from the import need checking"))
    root.div("card quality-note") {
        p(t("Kontroller varenummer, pris, enhet og eventuelle duplikater. Godkjenning blir auditert.", "Check item number, price, unit and any duplicates. Approval is logged in the audit log."), "small")
    }
    if (list.isEmpty()) {
        root.emptyCard(t("Alle produkter er kontrollert.", "All products are checked."))
        return
    }
    root.div("list") {
        list.forEach { product ->
            el("button", "card nav-card") {
                attr("type", "button")
                div("grow") {
                    div("strong", product.name)
                    div("muted small", "${product.category} · " + product.varenummer.ifBlank { t("uten varenummer", "no item number") })
                    div("warn-text tiny", product.dataQuality.ifBlank { t("Datakvalitet må gjennomgås", "Data quality must be reviewed") })
                }
                span("accent small strong", t("Kontroller ›", "Check ›"))
                onClick { productForm(product, product.category, user) }
            }
        }
    }
}
